import { DATE_FORMATS, FOCUS_RING, formatDate, Icon, Pills } from '@amelio/ui-library';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getMeetingStatusPill } from '@/pages/ThematicMeetings/utils/meetingSchedule';
import { toDateInputLang } from '@/utils/dateUtils';

interface MeetingStatusPillProps {
    meeting: ThematicMeeting;
    /** Opens the meeting's date for editing; only a planned meeting offers it. */
    onEditDate: () => void;
}

/**
 * The meeting's status and date, e.g. "Overdue · August 28, 2026 1:00 PM", in the same pill as its
 * card. While the meeting is planned, the whole pill is the button that changes the date; once
 * completed, it is a label.
 */
export const MeetingStatusPill = ({ meeting, onEditDate }: MeetingStatusPillProps) => {
    const { t } = useAmelioTranslations();
    // `fr_FR`, the language a French-from-France company reads in, is not a locale the library's
    // date helpers know: left as it is, the date comes back in English.
    const dateLanguage = toDateInputLang(useSelectedAppLanguage());
    const statusPill = getMeetingStatusPill(meeting);
    const isCompleted = statusPill.state === 'Completed';

    const schedule = meeting.scheduledAt
        ? formatDate({
              date: meeting.scheduledAt,
              format: DATE_FORMATS.DISPLAY_DATE_TIME,
              language: dateLanguage,
          })
        : t('thematicMeetings.plan.notScheduled');
    const statusLabel = t('thematicMeetings.plan.statusWithSchedule', {
        status: t(statusPill.labelKey),
        schedule,
    });

    const pill = (
        <Pills
            variant={statusPill.variant}
            dataTestId={'thematic-meeting-status-pill'}
        >
            {statusLabel}
            {!isCompleted && (
                <Icon
                    name={'edit-edit-2'}
                    size={'sm'}
                    ariaHidden
                />
            )}
        </Pills>
    );

    if (isCompleted) {
        return pill;
    }

    return (
        // The pill is the whole button: nothing around it is left unclickable.
        <button
            type={'button'}
            onClick={onEditDate}
            aria-haspopup={'dialog'}
            aria-label={t('thematicMeetings.plan.editDate', { label: statusLabel })}
            className={`${FOCUS_RING} cursor-pointer rounded-full`}
            data-testid={'thematic-meeting-date-button'}
        >
            {pill}
        </button>
    );
};
